import { CalendarClock, ChevronRight, IndianRupee, ReceiptText, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/Badge';
import { BarChart } from '@/components/ui/BarChart';
import { EmptyState } from '@/components/ui/EmptyState';
import { MetricTile } from '@/components/ui/MetricTile';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton, SkeletonLines } from '@/components/ui/Skeleton';
import { PAYMENT_STATUS_LABEL } from '@/constants/institute';
import type { ApiQueryState } from '@/hooks/useApiQuery';
import { formatCurrency, formatDate, formatMonth, formatNumber } from '@/lib/format';
import type { SubscriptionIncome } from '@/types/institute';

interface SubscriptionRevenuePanelProps {
  /** Fetched once by the dashboard and shared with the income strip above. */
  income: ApiQueryState<SubscriptionIncome>;
}

function institutes(count: number): string {
  return `${formatNumber(count)} ${count === 1 ? 'institute' : 'institutes'}`;
}

/**
 * The money the dashboard reads after the headline strip: what the book is
 * worth, what falls due this month, what renews soon, and a year of income.
 *
 * Every figure comes from `GET /platform/subscription-income`; nothing here is
 * summed in the browser, so the tiles and the server cannot disagree.
 */
export function SubscriptionRevenuePanel({ income }: SubscriptionRevenuePanelProps) {
  const { data, isLoading, error, refetch } = income;

  if (error) {
    return (
      <QueryError title="Could not load subscription revenue" error={error} onRetry={refetch} />
    );
  }

  const renewals = data?.upcomingRenewals;
  const monthly = data?.monthly ?? [];
  const hasIncome = monthly.some((point) => Number(point.amount) > 0);
  const thisMonthName = data ? formatMonth(data.month, false) : '';

  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MetricTile
          tone="indigo"
          icon={<IndianRupee className="h-5 w-5" />}
          label="Annual contract value (live and onboarding)"
          value={formatCurrency(data?.currentRevenue.amount ?? '0')}
          hint={
            data ? `Yearly fees · ${institutes(data.currentRevenue.instituteCount)}` : undefined
          }
          isLoading={isLoading}
        />
        <MetricTile
          tone="amber"
          icon={<ReceiptText className="h-5 w-5" />}
          label="Due this month"
          value={formatCurrency(data?.dueThisMonth.amount ?? '0')}
          hint={
            data
              ? `Unpaid, falling due by the end of ${thisMonthName} · ${institutes(data.dueThisMonth.instituteCount)}`
              : undefined
          }
          isLoading={isLoading}
        />
        <MetricTile
          tone="sky"
          icon={<RefreshCw className="h-5 w-5" />}
          label="Upcoming renewals"
          value={formatCurrency(renewals?.amount ?? '0')}
          hint={
            renewals
              ? `Next ${renewals.windowDays} days · ${institutes(renewals.instituteCount)}`
              : undefined
          }
          isLoading={isLoading}
        />
      </div>

      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-5">
        <section className="card flex flex-col gap-3 p-4 sm:p-5 lg:col-span-3">
          <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
            <div>
              <h3 className="text-base font-semibold text-ink">Monthly income</h3>
              <p className="mt-0.5 text-sm text-ink-3">
                Licence payments received in each of the last {monthly.length || 12} months.
              </p>
            </div>
            {/* Every payment behind these bars, one row each. */}
            <Link
              to="/platform/subscriptions"
              className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
            >
              View all transactions
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>
          {isLoading ? (
            <Skeleton className="h-[200px] w-full" />
          ) : hasIncome ? (
            <BarChart
              title="Licence payments received per month"
              series={[{ key: 'amount', label: 'Received', tone: 1 }]}
              points={monthly.map((point) => ({
                label: formatMonth(point.month),
                axisLabel: formatMonth(point.month, false),
                values: { amount: Number(point.amount) },
              }))}
              formatValue={(value) => formatCurrency(value)}
            />
          ) : (
            <EmptyState
              icon={<IndianRupee className="h-5 w-5" />}
              title={
                monthly.length > 0
                  ? `No payments in the last ${monthly.length} months`
                  : 'No payments recorded'
              }
              description="Payments recorded against an institute's subscription appear here by the month they were paid."
            />
          )}
        </section>

        <section className="card flex flex-col gap-3 p-4 sm:p-5 lg:col-span-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-ink">Upcoming renewals</h3>
              <p className="mt-0.5 text-sm text-ink-3">
                Due in the next {renewals?.windowDays ?? 30} days, soonest first.
              </p>
            </div>
            <CalendarClock className="h-5 w-5 shrink-0 text-ink-3" aria-hidden />
          </div>

          {isLoading ? (
            <SkeletonLines count={4} />
          ) : renewals && renewals.institutes.length > 0 ? (
            <>
              <ul className="flex flex-col divide-y divide-line">
                {renewals.institutes.map((institute) => (
                  <li key={institute.uuid}>
                    <Link
                      to={`/platform/institutes/${institute.uuid}`}
                      className="group flex items-center justify-between gap-3 rounded-field py-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink">
                          {institute.displayName}
                        </p>
                        <p className="truncate text-xs text-ink-3">
                          {institute.city} · due {formatDate(institute.dueDate)}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <span className="text-sm font-semibold text-ink">
                          {institute.subscriptionAmount === null
                            ? 'No fee set'
                            : formatCurrency(institute.subscriptionAmount)}
                        </span>
                        <Badge tone={institute.paymentStatus === 'PAID' ? 'success' : 'warning'}>
                          {PAYMENT_STATUS_LABEL[institute.paymentStatus]}
                        </Badge>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
              {renewals.instituteCount > renewals.institutes.length && (
                <Link
                  to="/platform/institutes"
                  className="inline-flex items-center gap-1 self-start text-sm font-medium text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
                >
                  {/* The list has no due-date filter, so this names where it goes
                      rather than promising the remaining renewals. */}
                  {formatNumber(renewals.instituteCount - renewals.institutes.length)} more · View
                  institutes
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </Link>
              )}
            </>
          ) : (
            <EmptyState
              icon={<RefreshCw className="h-5 w-5" />}
              title={
                renewals
                  ? `No renewals in the next ${renewals.windowDays} days`
                  : 'No upcoming renewals'
              }
              description="Institutes whose subscription falls due soon will be listed here."
            />
          )}
        </section>
      </div>
    </div>
  );
}
